import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useDirectoryApiInfo } from '@my/directory-api/hooks';
import type { ApiInfo as DirectoryApiInfo } from '@my/directory-api';
import { PageHeader } from '@my/ui';
import { Check } from '@/api/agent';
import type { ApiInfo as CheckApiInfo } from '@/api/agent';
import { getConfig } from '@/configuration/config';

/**
 * Gateway/auth smoke-test page. Lives at `/_diagnostics` so we can verify the
 * full request path (token, X-App-Key, proxy → gateway → API) end-to-end
 * without occupying the real dashboard URL.
 */
export function Diagnostics() {
  const check = useQuery<CheckApiInfo>({
    queryKey: ['check', 'api-info'],
    queryFn: () => Check.getApiInfo(),
    staleTime: 60_000,
  });

  const directory = useDirectoryApiInfo();
  // The same config that bootstrap()/Auth0Provider read from /config.json —
  // surface here so support tickets can include "what version is the FE?"
  // without DevTools archaeology. `environment` is the free-form label
  // (DEV/TEST/PROD); `version` is whatever the build pipeline stamped.
  const cfg = getConfig();

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <PageHeader
        title="Diagnostics"
        description="Each card hits an info/api-info endpoint through the gateway."
      />

      {/* FE build banner — colour-coded by environment so a support
          screenshot makes the env obvious at a glance even for someone
          who doesn't know the URLs by heart. */}
      <section className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Check Frontend Build
        </span>
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${envBadgeClass(
            cfg.environment,
          )}`}
        >
          {cfg.environment || 'unknown'}
        </span>
        <span className="text-xs text-slate-500">version</span>
        <code className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
          {cfg.version || 'unknown'}
        </code>
      </section>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <ApiInfoCard title="Check API" path="/check/api/v1/info/api-info" query={check} />
        <ApiInfoCard
          title="Directory API"
          path="/directory/api/v1/info/api-info"
          query={directory}
        />
      </div>
    </div>
  );
}

/**
 * Visual cue per environment. Colour is intentional, not just decorative:
 * a screenshot from PROD should be unmistakeable from one in DEV.
 */
function envBadgeClass(env: string): string {
  const upper = env.trim().toUpperCase();
  if (upper === 'PROD' || upper === 'PRODUCTION') return 'bg-red-100 text-red-700';
  if (upper === 'TEST' || upper === 'STAGING' || upper === 'STAGE')
    return 'bg-amber-100 text-amber-700';
  if (upper === 'DEV' || upper === 'DEVELOPMENT') return 'bg-emerald-100 text-emerald-700';
  return 'bg-slate-100 text-slate-700';
}

interface CardProps<T> {
  title: string;
  path: string;
  query: UseQueryResult<T, Error>;
}

function ApiInfoCard<T extends CheckApiInfo | DirectoryApiInfo>({
  title,
  path,
  query,
}: CardProps<T>) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <header className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
        <code className="text-xs text-slate-500">{path}</code>
      </header>
      {query.isPending && <p className="text-slate-500">Loading…</p>}
      {query.isError && <p className="text-sm text-red-600">{query.error.message}</p>}
      {query.isSuccess && (
        <pre className="overflow-x-auto rounded bg-slate-900 p-4 text-xs text-slate-100">
          {JSON.stringify(query.data, null, 2)}
        </pre>
      )}
    </section>
  );
}
